import { Articulation } from 'types/articulation';
import { FormSchema } from 'types/form-schema';
import { Precedent } from 'types/precedent';
import { getTotalCreditPoints as getArticulationTotalCreditPoints } from 'utils/articulation';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import { getTotalCreditPoints } from './precedent';
import { pluralize } from 'utils/general';

export const getArticulationsAltText = (
  articulations: Articulation[],
  formSchema: FormSchema,
  isDetails = false,
): string => {
  const multipleArticulations = articulations.length > 1;
  const requiredCredentials = articulations[0].requiredCredentials;
  const multipleRequiredCredentials = requiredCredentials.length > 1;

  const requiredCredentialsText = requiredCredentials.map((credential) => {
    const { heading } = getHeadingAndSubHeading(credential, formSchema);
    return heading;
  });

  const pluralizedRequiredCredentialsText = pluralize(requiredCredentialsText);

  let isGuaranteedEntryOnly = false;

  const courseTexts = pluralize(
    articulations.map((art) => {
      isGuaranteedEntryOnly = art.arrangements.every((a) => a.guaranteedEntryOnly);
      if (isGuaranteedEntryOnly) {
        return `guaranteed entry to ${art.course.name} - ${art.course.code}`;
      }
      return `${getArticulationTotalCreditPoints(art)} credit points for ${art.course.name} - ${art.course.code}`;
    }),
  );

  const exemptedSubjects: string[] = [];
  articulations.forEach((art) => {
    art.arrangements.forEach((a) => {
      a.subjects.forEach((sub) => {
        exemptedSubjects.push(`${sub.name} - ${sub.code}`);
      });
    });
  });

  let altText = '';
  if (!isDetails) {
    let beginning = `By completing ${requiredCredentials.length} credential${
      multipleRequiredCredentials ? 's' : ''
    } ${pluralizedRequiredCredentialsText}, you may receive`;
    if (multipleArticulations) {
      beginning = `${beginning}:`;
    }
    altText = `${beginning} ${courseTexts}.`;
  } else {
    // Need to check for this condition FIRST.
    if (isGuaranteedEntryOnly) {
      altText = `By completing ${requiredCredentials.length} credential${
        multipleRequiredCredentials ? 's' : ''
      }, ${pluralizedRequiredCredentialsText}, you may receive ${courseTexts}.`;
    } else {
      if (isDetails) {
        altText = `By completing ${requiredCredentials.length} credential${
          multipleRequiredCredentials ? 's' : ''
        } ${pluralizedRequiredCredentialsText}, you may receive credit for ${courseTexts}. You may be exempted from taking ${
          exemptedSubjects.length
        } units: ${pluralize(exemptedSubjects)}.`;
      } else {
        altText = `By completing ${requiredCredentials.length} credential${
          multipleRequiredCredentials ? 's' : ''
        } ${pluralizedRequiredCredentialsText}, you may receive ${courseTexts}.`;
      }
    }
  }
  return altText;
};

export const getPrecedentAltText = (precedent: Precedent, formSchema: FormSchema, institution: string): string => {
  const requiredCredentialsText = precedent.requiredCredentials.map((credential) => {
    const { heading } = getHeadingAndSubHeading(credential, formSchema);
    return heading;
  });

  const requiredCredentials = pluralize(requiredCredentialsText);
  const creditPoints = getTotalCreditPoints(precedent);

  const unitTexts = precedent.subjects.map((sub) => `${sub.name} - ${sub.code}`);

  let exemptedUnits = `${precedent.subjects.length} unit`;
  if (precedent.subjects.length > 1) {
    exemptedUnits = exemptedUnits + 's';
  }

  const altText = `By completing ${precedent.requiredCredentials.length} credential${
    precedent.requiredCredentials.length > 1 ? 's' : ''
  } ${requiredCredentials}, you may receive ${creditPoints} credit points. You may be exempted from taking ${exemptedUnits} at ${institution}: ${unitTexts}.`;
  return altText;
};
